<template lang="pug">
FetchErrorTransition(:isFetching="isFetching" :error="fetchError")
  UpsertDeal(
    v-if="deal"
    :deal="deal"
    :products="products"
    :businessUnit="businessUnit"
    :funnelBaseUrl="funnelBaseUrl"
    :startOfDay="startOfDay"
    :isSaving="isSaving"
    @save="save"
    @cancel="backToOverview()"
    :canViewAuditTrail="showAuditTrail"
    :enableMultilanguage="enableMultilanguage"
    )
  p(v-else style="margin-top: 30px") This deal could not be found!
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import UpsertDeal from './UpsertDeal.vue'
import {
  DotnetValidationError,
  ExceptionOrigin,
  parseAxiosException,
} from '@/helpers/exceptionParser.js'
import { startsWith, assoc, set, lensProp } from 'ramda'

export default {
  name: 'UpsertDealWrapper',
  components: { UpsertDeal },
  inject: ['upsertDealService'],
  props: {
    dealKey: String,
  },
  data() {
    return {
      isFetching: true,
      isSaving: false,
      fetchError: null,
      saveError: null,
      response: null,
      deal: null,
      products: null,
      businessUnit: null,
      funnelBaseUrl: null,
      startOfDay: null,
    }
  },
  computed: {
    ...mapState('imagePicker', ['removeExistingFile']),
    ...mapState('inventory', ['deals']),
    ...mapGetters('briqState', ['userCanViewAuditTrail', 'enabledFeatures', 'envFeatureFlags']),
    enableMultilanguage() {
      return this.envFeatureFlags['enable-multilanguage']
    },
    showAuditTrail() {
      return this.deal.Key && this.userCanViewAuditTrail
    },
  },
  methods: {
    ...mapActions('imagePicker', ['maybeSaveImage', 'initStateData']),
    ...mapActions('inventory', ['updateDeal']),
    async save() {
      if (this.isSaving) {
        this.$logWarning('Save in progress')
        this.$notifyWarning('Can not save while saving is already in progress')
      }
      if (this.fetchError) {
        this.$logWarning('Can not save while there was a fetch error')
        this.$logWarning('save() was called while there was a fetchError in UpsertDealWrapper')
      }
      this.isSaving = true
      try {
        const oldImageUrl = this.deal.ImageLocation
        if (this.removeExistingFile) {
          this.deal.ImageLocation = null
        }
        const newImageUrl = await this.maybeSaveImage()
        if (newImageUrl) this.deal.ImageLocation = newImageUrl
        else if (oldImageUrl) {
          // todo: make a delete image call once we have a delete image endpoint
        }
        const result = await this.upsertDealService.save(this.deal, this.enableMultilanguage)
        this.updateDeal(result.data)
        this.$notifySuccess('Deal saved')
        this.backToOverview()
      } catch (e) {
        this.handleError(e)
      }
      this.isSaving = false
    },

    handleError(e) {
      const parsedException = parseAxiosException(e)
      this.saveError = parsedException
      this.$logError(`Error expected to originate from ${parsedException.origin}`)
      this.$logError(parsedException.data)
      const msg = this.getErrorMessage(parsedException)
      this.$notifyError(msg)
    },

    getErrorMessage(parsedException) {
      if (parsedException.status === 400) return this.getBadRequestMessage(parsedException)
      return 'Something went wrong'
    },

    getBadRequestMessage(parsedException) {
      if (parsedException.origin === ExceptionOrigin.DOTNET) {
        /** @type { DotnetValidationError } */
        const dotnetError = parsedException.data
        if (dotnetError.AdditionalInformation?.Products === 'Deleted products already in use') {
          // eslint-disable-next-line quotes
          return "Product cannot be removed while it's already in use!"
        }
        if (
          startsWith(
            'Duplicate slug',
            dotnetError.AdditionalInformation?.Slug
              ? dotnetError.AdditionalInformation?.Slug[0]
              : '',
          )
        ) {
          return 'The provided slug is already in use by another deal'
        }
        if (dotnetError.AdditionalInformation?.MaxPersons?.length > 0) {
          return dotnetError.AdditionalInformation?.MaxPersons[0]
        }
      }
      return 'Something went wrong'
    },

    backToOverview() {
      this.$router.push({ name: 'Deals' })
    },

    // todo: refactor to UpsertDealService (using Result.map)
    stripNonExistingFilters({ deal, businessUnit }) {
      if (businessUnit?.Filters === null)
        return set(lensProp('Filters'), businessUnit?.Filters, deal)
      const existingFilters = businessUnit?.Filters
        ? Object.values(businessUnit.Filters).map(f => f.slug)
        : []
      const dealFilters = deal.Filters
        ? existingFilters.reduce((a, n) => assoc(n, deal.Filters[n], a), {})
        : null

      let dealFiltersFiltered = null
      if (dealFilters !== null) {
        dealFiltersFiltered = {}
        for (const [key, value] of Object.entries(dealFilters)) {
          dealFiltersFiltered[key] = typeof value !== 'undefined' ? value : []
        }
      }

      return set(lensProp('Filters'), dealFiltersFiltered, deal)
    },
  },
  async mounted() {
    this.isFetching = true
    const result = await this.upsertDealService.fetchEditModel(this.dealKey)
    result.matchWith({
      Ok: ({ value }) => {
        this.response = value
        this.deal = this.stripNonExistingFilters(value.data)
        this.products = value.data.products
        this.businessUnit = value.data.businessUnit
        this.funnelBaseUrl = value.data.funnelBaseUrl
        this.startOfDay = value.data.startOfDay
        if (this.dealKey) this.$setDocumentTitle(`Deals | ${this.deal.InternalName}`)
      },
      Error: ({ value }) => {
        const message =
          value.response?.status === 401
            ? 'You are not authorized to view this information'
            : 'Something went wrong'
        this.$notifyError(message)
        this.$logError(value)
        this.fetchError = value
      },
    })
    this.isFetching = false
    this.initStateData()
  },
}
</script>
